/**
 * The latest TradeFinder signals for the chart's root, as a compact strip
 * over the top of the chart (desktop and phone): glyph, side, age, edge t,
 * price and the key level it sits on. Click = centre the chart on it and
 * blink its marker.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useFlowSignalsStore, EDGE_T_RELIABLE, isMboContextKind, mboContextText, type FlowSignal } from '../../store/flowSignalsStore';
import { useTradeFinderUxStore } from '../../store/tradeFinderUxStore';
import { normalizeToBase } from '../../utils/symbolUtils';
import type { KeyLevel } from '../../utils/keyLevels';
import { fmtAge, fmtT, levelTolFor, stripEntries } from './tradeFinderUx';
import './TradeFinderStrip.css';

const EMPTY: FlowSignal[] = [];

interface Props {
  symbol: string;
  /** Kinds shown on the chart (levelsStore flow* keys, master applied). */
  shown: Partial<Record<string, boolean>>;
  /** Key levels for the "at PW" match. */
  levels: readonly KeyLevel[];
  onSelect?: (s: FlowSignal) => void;
  compact?: boolean;
}

/** Ticks once a second while mounted, for the age counters. */
function useNow(periodMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), periodMs);
    return () => clearInterval(id);
  }, [periodMs]);
  return now;
}

const TradeFinderStrip: React.FC<Props> = ({ symbol, shown, levels, onSelect, compact }) => {
  const root = normalizeToBase(symbol);
  const signals = useFlowSignalsStore((f) => f.bySymbol[root] ?? EMPTY);
  const count = useTradeFinderUxStore((s) => s.stripCount);
  const tolMap = useTradeFinderUxStore((s) => s.levelTol);
  const now = useNow();
  const tol = levelTolFor(root, tolMap);
  const entries = useMemo(
    () => stripEntries(signals, shown, count, now, levels, tol),
    [signals, shown, count, now, levels, tol],
  );

  return (
    <div className={`tf-strip ${compact ? 'is-compact' : ''}`} data-testid="tradefinder-strip" role="list" aria-label="Latest TradeFinder signals">
      {entries.length === 0 ? (
        <span className="tf-strip-empty" data-testid="tradefinder-strip-empty">TradeFinder · no signals yet</span>
      ) : entries.map((e) => (
        <button
          key={e.signal.id}
          type="button"
          role="listitem"
          className={`tf-chip is-${e.reading} ${e.level ? 'at-level' : ''}`}
          onClick={() => onSelect?.(e.signal)}
          title={`${e.signal.label || e.signal.kind} · ${e.reading} · ${e.signal.price.toFixed(2)}${e.level ? ` at ${e.level.code} ${e.level.price.toFixed(2)}` : ''}`}
          data-testid="tradefinder-strip-item"
          data-signal-id={e.signal.id}
          data-level={e.level?.code ?? ''}
          data-kind={e.signal.kind}
        >
          <span className="tf-chip-glyph" aria-hidden="true">{e.glyph}</span>
          <span className={`tf-chip-side is-${e.reading}`}>{e.side}</span>
          <span className="tf-chip-age" data-testid="tradefinder-strip-age">{fmtAge(e.ageMs)}</span>
          {e.context && e.edgeT === null ? (
            <span
              className="tf-chip-edge is-context"
              title={isMboContextKind(e.signal.kind)
                ? mboContextText(e.signal)
                : 'context signal (unproven, no tuner edge): not an entry'}
              data-testid="tradefinder-strip-edge"
            >
              context
            </span>
          ) : (
            <span
              className={`tf-chip-edge ${e.reliable ? 'is-reliable' : 'is-weak'}`}
              title={e.edgeT === null ? 'no edge data for this kind' : `tuner edge t ${fmtT(e.edgeT)}; reliable at |t| >= ${EDGE_T_RELIABLE}`}
              data-testid="tradefinder-strip-edge"
            >
              {e.edgeT === null ? 'no edge data' : `t ${fmtT(e.edgeT)}`}
            </span>
          )}
          <span className="tf-chip-price">{e.signal.price.toFixed(2)}</span>
          {e.level && <span className="tf-chip-level" data-testid="tradefinder-strip-level">at {e.level.code}</span>}
        </button>
      ))}
    </div>
  );
};

export default TradeFinderStrip;
